import { RootState } from 'src/redux/root'
import { useTemplateDatabase } from 'src/redux/services/ubem'
import { uploadVizFile } from 'src/redux/slices/analysis/endpoints'
import { getProject } from 'src/redux/slices/project/endpoints'
import {
  Entrypoints,
  KeyField,
  KeyFields,
  KeyFieldsConfig,
} from 'src/redux/slices/umibuilder/consts'
import {
  confirmKeyFields,
  exportExchangeFile,
  exportKeyFieldsErrorReport,
  mutationFixedCacheKeys,
  sendGroupData,
} from 'src/redux/slices/umibuilder/endpoints'
import { errorAutoHandlingMessages } from 'src/redux/slices/umibuilder/utils'
import { getEntries } from 'src/redux/utils'

import { KeyFieldConfiguration } from './types'

export const selectEntrypoint = (state: RootState) => state.umibuilder.entrypoint

export const isLoading = (state: RootState) => {
  const baseFileUploading = Entrypoints.map((entrypoint) => uploadVizFile.select(entrypoint)(state))
    .map(({ isLoading }) => isLoading)
    .some((isLoading) => isLoading)
  const uioIsExporting = exportExchangeFile.select(mutationFixedCacheKeys.exportExchangeFile)(
    state,
  ).isLoading
  const keyFieldsErrorsAreExporting = exportKeyFieldsErrorReport.select(
    mutationFixedCacheKeys.exportKeyFieldsErrorReport,
  )(state).isLoading
  const keyFieldsAreConfirming = confirmKeyFields.select(mutationFixedCacheKeys.confirmKeyFields)(
    state,
  ).isLoading
  const groupDataIsSending = sendGroupData.select(mutationFixedCacheKeys.sendGroupData)(
    state,
  ).isLoading
  const isLoadingDatabaseTemplateLibrary = useTemplateDatabase.select('')(state).isLoading
  const isLoadingProject = getProject.select('')(state).isLoading
  return (
    state.umibuilder.isProcessing ||
    baseFileUploading ||
    uioIsExporting ||
    keyFieldsErrorsAreExporting ||
    keyFieldsAreConfirming ||
    groupDataIsSending ||
    isLoadingProject ||
    isLoadingDatabaseTemplateLibrary
  )
}

export const selectUmiBuilderFromCloud = (state: RootState) => state.umibuilder.fromCloud

export const selectGeoJSON = (state: RootState) => {
  return state.umibuilder.geoJSON
}
export const selectIsLargeGeoJSON = (state: RootState) => {
  const project = getProject.select('')(state).data
  const largeGeoJSON = project?.largeGeoJSON ?? state.umibuilder.largeGeoJSON
  return largeGeoJSON
}

export const selectClimateZones = (state: RootState) => {
  const project = getProject.select('')(state).data
  const climateZones = project?.climateZones ?? state.umibuilder.climateZones
  return climateZones
}

export const selectBounds = (state: RootState) => {
  const project = getProject.select('')(state).data
  if (project?.mapBounds) {
    const projectMapBounds = project.mapBounds as [number, number, number, number]
    return [
      [projectMapBounds[1], projectMapBounds[0]],
      [projectMapBounds[3], projectMapBounds[2]],
    ]
  }
  return state.umibuilder.mapBounds
}

// TODO: should be memoized
export const makeSelectAvailableColumns = (field: keyof typeof KeyField) => (state: RootState) => {
  const project = getProject.select('')(state).data ?? {
    categoricalProps: [],
    shapefileProps: [],
    numericalProps: [],
  }
  // TODO: better initialization so that ?? [] is not needed
  const projectColumns = [...(project?.[KeyFieldsConfig[field].propTypes] ?? [])]
  const localColumns = [...(state.umibuilder[KeyFieldsConfig[field].propTypes] ?? [])]
  const columns = [...new Set([...projectColumns, ...localColumns])]
  if (!KeyFieldsConfig[field].isRequired) columns.push('N/A')
  return columns
}

// TODO: should be memoized
export const selectKeyFields = (state: RootState) => {
  const localAssignments = state.umibuilder.keyFields
  const project = getProject.select('')(state).data ?? { keyFields: {} }
  const assignments = {
    ...localAssignments,
    ...((project?.keyFields as KeyFieldConfiguration | undefined) ?? {}),
  }
  getEntries(localAssignments).forEach(([key, val]) => {
    if (val) assignments[key] = val
  })
  return assignments
}

export const makeSelectKeyFieldAssignment =
  (field: keyof typeof KeyField) => (state: RootState) => {
    const keyFields = selectKeyFields(state)
    return keyFields[field]
  }

export const allFieldsAssigned = (state: RootState) => {
  const keyFields = selectKeyFields(state)
  return KeyFields.filter((field) => KeyFieldsConfig[field].isRequired).every(
    (field) => keyFields[field],
  )
}

export const selectErrorSummary = (state: RootState) => state.umibuilder.keyFieldsErrorSummary

export const makeSelectKeyFieldErrorMessage =
  (keyField: keyof typeof KeyField) => (state: RootState) => {
    // TODO: this should probably be done in utils error tools!
    const summary = selectErrorSummary(state)
    if (keyField !== KeyField.id) {
      return summary[keyField]
        ? `${summary[keyField]} error${summary[keyField] !== 1 ? 's' : ''}`
        : ''
    } else {
      const duplicateErrors = summary.duplicateId ? `${summary.duplicateId} duplicate IDs` : ''
      const missingIds = summary.id ? `${summary.id} error${summary.id !== 1 ? 's' : ''}` : ''
      return [duplicateErrors, missingIds].filter((a) => a).join(', ')
    }
  }
export const selectKeyFieldErrorBypassMessages = (state: RootState) => {
  const errorMessages = []
  KeyFields.forEach((keyField) => {
    const message = makeSelectKeyFieldErrorMessage(keyField)(state)
    if (!message) return

    errorMessages.push({
      automatedAction: errorAutoHandlingMessages[keyField],
      summary: `${keyField.toUpperCase()}: ${message}`,
    })
  })
  if (state.umibuilder.keyFieldsErrorSummary.overlap) {
    errorMessages.push({
      automatedAction:
        'Overlapping buildings may cause errors in your UMI simulations!  The buildings should be addressed in the GIS file.',
      summary: `Overlap: ${state.umibuilder.keyFieldsErrorSummary.overlap} errors`,
    })
  }
  return errorMessages
}

export const selectContextLayers = (state: RootState) => state.umibuilder.contextLayers

export const selectGroupData = (state: RootState) => state.umibuilder.groups

export const makeSelectArchetypeSubgroup =
  ({
    field,
    archetypeID,
  }: {
    field: string | null | undefined
    archetypeID: string | null | undefined
  }) =>
  (state: RootState) => {
    const primaryGroup = state.umibuilder.groups?.find(({ field: _field }) => field === _field)
    const { groups } = primaryGroup ?? {}
    const group = groups?.find(({ archetypeID: id }) => id === archetypeID)
    return group
  }

export const selectSummaryData = (state: RootState) => state.umibuilder.summary

export const selectUmiReady = (state: RootState) => state.umibuilder.umiReady

export const selectTemplateLibraryFilters = (state: RootState) => state.umibuilder.libraryFilters

export const selectDisabledTabs = (state: RootState) => {
  const entrypoint = selectEntrypoint(state)
  // TODO: The pages should be declared outside of the component to make these accessible
  return {
    upload: false,
    keyfields: !selectGeoJSON(state),
    organize: !selectGroupData(state),
    assign: !selectGroupData(state),
    context: !selectSummaryData(state),
    export: !selectSummaryData(state),
  }
}
